import React from 'react';
import { format, parseISO } from 'date-fns';
import { ChartContainer, ChartTooltip } from '@/shared/ui/chart';
import { XAxis, YAxis, CartesianGrid, Bar, BarChart, Legend } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { formatDataUsage } from '@/shared/utils/format-utils';
import EmptyView from './EmptyView';
import { BAR_CHART_CATEGORY_GAP, BAR_CHART_MAX_BAR_SIZE } from '@/domains/global-dashboard/utils/chart-utils';
import { DailyDataUsage } from '@/shared/types/serviceLineTypes';
import { DataConsumptionType, getDataConsumptionTypeChartFill } from '@/shared/utils/color-utils-data-usages';

const STANDARD_DATA_FILL = getDataConsumptionTypeChartFill(DataConsumptionType.STANDARD_DATA);
const PRIORITY_DATA_FILL = getDataConsumptionTypeChartFill(DataConsumptionType.PRIORITY_DATA);

interface DailyDataUsageBarChartProps {
    dataUsages: DailyDataUsage[];
}

export function DailyDataUsageBarChart({ dataUsages }: DailyDataUsageBarChartProps) {

    const chartData = React.useMemo(() => {
        return dataUsages.length > 0
            ? dataUsages
                .slice()
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                .map((usage) => {
                    const priorityData = usage.totalDataUsageGB ?? 0;
                    const standardData = usage.standardGB ?? 0;
                    return {
                        month: format(parseISO(usage.date), 'MMM yyyy'),
                        shortMonth: format(parseISO(usage.date), 'MMM'),
                        date: usage.date,
                        shortDate: format(parseISO(usage.date), 'd'),
                        standardData,
                        priorityData
                    };
                })
            : [];
    }, [dataUsages]);

    return (
        <div className="h-96">
            {chartData.length > 0 ? (
                <ChartContainer
                    config={{
                        standardData: {
                            label: 'Standard Data',
                            color: STANDARD_DATA_FILL,
                        },
                        priorityData: {
                            label: 'Priority Data',
                            color: PRIORITY_DATA_FILL,
                        },
                    }}
                    className="w-full h-full"
                >
                    <BarChart
                        data={chartData}
                        margin={{ top: 40, right: 10, left: 10, bottom: 10 }}
                        maxBarSize={BAR_CHART_MAX_BAR_SIZE}
                        barCategoryGap={BAR_CHART_CATEGORY_GAP}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
                        <XAxis
                            dataKey="shortDate"
                            tick={{ fontSize: 12, fontWeight: 'bold' }}
                            angle={0}
                            textAnchor="middle"
                            height={20}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fontWeight: 'bold' }}
                            tickFormatter={(value) => formatDataUsage(Number(value))}
                            domain={[0, 'auto']}
                        />
                        <ChartTooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const data = payload[0].payload as {
                                    date: string;
                                    standardData: number;
                                    priorityData: number;
                                };
                                return (
                                    <div className="rounded-lg border bg-background p-3 shadow-md">
                                        <div className="grid gap-2">
                                            <span className="text-sm font-medium">
                                                {format(parseISO(data.date), 'MMM d, yyyy')}
                                            </span>
                                            <div className="flex items-center gap-2 text-xs">
                                                <span
                                                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                                    style={{ background: PRIORITY_DATA_FILL }}
                                                />
                                                <span className="text-muted-foreground">Priority Data</span>
                                                <span className="ml-auto font-semibold">
                                                    {formatDataUsage(Number(data.priorityData))}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs">
                                                <span
                                                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                                    style={{ background: STANDARD_DATA_FILL }}
                                                />
                                                <span className="text-muted-foreground">Standard Data</span>
                                                <span className="ml-auto font-semibold">
                                                    {formatDataUsage(Number(data.standardData))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <Legend
                            content={() => (
                                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 pt-2 text-xs">
                                    <span className="inline-flex items-center gap-2">
                                        <span
                                            className="h-3 w-3 shrink-0 rounded-sm"
                                            style={{ background: PRIORITY_DATA_FILL }}
                                        />
                                        Priority Data
                                    </span>
                                    <span className="inline-flex items-center gap-2">
                                        <span
                                            className="h-3 w-3 shrink-0 rounded-sm"
                                            style={{ background: STANDARD_DATA_FILL }}
                                        />
                                        Standard Data
                                    </span>
                                </div>
                            )}
                        />
                        <Bar
                            dataKey="standardData"
                            stackId="daily"
                            name="Standard Data"
                            fill={STANDARD_DATA_FILL}
                        />
                        <Bar
                            dataKey="priorityData"
                            stackId="daily"
                            name="Priority Data"
                            fill={PRIORITY_DATA_FILL}
                        />
                    </BarChart>
                </ChartContainer>
            ) : (
                <EmptyView
                    icon={BarChart3}
                    title="No Usage Data Available"
                    description="This service line doesn't have any usage data to display."
                />
            )}
        </div>
    );
}
